import { getHasRelatedProducts } from '@app/utils/guards';
import { isDefined } from '@app/utils/is';
import { Badges, Media, Product } from '@typings';
import { path } from 'ramda';
import { Option } from 'space-lift';

import { getProductImageOrPlaceholderBySize } from '../helpers/images';

const getGallerySlide = (product: Product.Standard, defaultImageSize: Media.Size): Product.GallerySlide => {
  const { product: productId, variant: variantId } = product;

  return {
    id: `${productId}-${variantId}`,
    image: getProductImageOrPlaceholderBySize(product, defaultImageSize),
    isAvailable: getProductAvailability(product),
    swatchData: product.sh_swatch,
    variant: product,
  };
};

export const getGallerySlides = (product: Product.Standard, defaultImageSize: Media.Size): Product.GallerySlide[] => {
  return getProductFamily(product).map(item => getGallerySlide(item, defaultImageSize));
};

export const getCustomBadges = ({ sh_custom_badge = [] }: Product.Standard): Badges.CustomBadge[] => {
  return sh_custom_badge.map(({ background_color: backgroundColor, text_color: textColor, ...rest }) => ({
    backgroundColor,
    textColor,
    ...rest,
  }));
};

export const getIsDefaultBadge = (badge: Badges.AnyBadge): badge is Badges.DefaultBadge => {
  return 'badgeType' in badge;
};

export function getProductAvailability(product: Product.Standard | Product.Full, delwinIdsToCheck?: string[]): boolean {
  const availableByDeliveryWindowPrimaryVariant = 'availableByDeliveryWindow' in product ? product.availableByDeliveryWindow : undefined;

  /*
   * Either the API didn't send `availableByDeliveryWindow`
   * or the user is browsing lookbooks. Fall back to `true`.
   */
  if (availableByDeliveryWindowPrimaryVariant === undefined) {
    return true;
  }

  if (isDefined(delwinIdsToCheck)) {
    return delwinIdsToCheck.some(delwinId => availableByDeliveryWindowPrimaryVariant[delwinId]);
  }

  return Object.values(availableByDeliveryWindowPrimaryVariant).includes(true);
}

/**
 * Returns a flat list of product and all its variants.
 */
export function getProductFamily(product: Product.Standard): (Product.Standard | Product.Full)[] {
  return [product, ...Option<Product.Full[]>(path(['relatedProducts', 'variants'], product)).getOrElse([])];
}

export function getHasAvailableVariants(products: (Product.Standard | Product.Full)[], delwinIdsToCheck?: string[]): boolean {
  return products.map(product => getProductAvailability(product, delwinIdsToCheck)).some(availability => availability);
}

export const getNativeVariants = (productDetails: Product.Standard | Product.Full, productId: string): Product.Standard[] => {
  if (!getHasRelatedProducts(productDetails)) {
    return [];
  }

  return [productDetails, ...productDetails.relatedProducts.variants]
    .filter(relatedProduct => relatedProduct.product === productId)
    .map(product => ({ ...product, relation: 'variant' }));
};
